import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useProducts, useCustomers, productOptions, customerOptions, nameOf, REVENUE_CATEGORIES, BILLING_CYCLES } from "@/lib/lookups";

export default function Revenue() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["revenues"], queryFn: async () => (await api.get("/revenues")).data });
  const { data: products } = useProducts();
  const { data: customers } = useCustomers();

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? api.put(`/revenues/${editing.id}`, payload) : api.post("/revenues", payload),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Revenue saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/revenues/${id}`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Revenue voided"); },
  });

  const fields = [
    { name: "date", label: "Date", type: "date", full: false },
    { name: "reference", label: "Invoice / Reference", placeholder: "INV-0001" },
    { name: "customer_id", label: "Customer", type: "select", options: customerOptions(customers) },
    { name: "product_id", label: "Product", type: "select", options: productOptions(products) },
    { name: "category", label: "Category", type: "select", options: REVENUE_CATEGORIES, default: "saas_subscription" },
    { name: "amount_ex", label: "Amount (ex-GST)", type: "number", step: "0.01", placeholder: "0.00" },
    { name: "gst_treatment", label: "GST Treatment", type: "select", options: [
      { value: "taxable", label: "Taxable (10%)" }, { value: "gst_free", label: "GST-free" }] , default: "taxable" },
    { name: "recurring", label: "Recurring revenue", type: "switch" },
    { name: "billing_cycle", label: "Billing Cycle", type: "select", options: BILLING_CYCLES, default: "monthly" },
    { name: "payment_status", label: "Payment Status", type: "select", options: [
      { value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }, { value: "overdue", label: "Overdue" }], default: "unpaid" },
    { name: "due_date", label: "Due Date", type: "date" },
    { name: "payment_date", label: "Payment Date", type: "date" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];

  const columns = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: "reference", header: "Reference" },
    { key: "customer_id", header: "Customer", render: (r) => nameOf(customers, r.customer_id) },
    { key: "product_id", header: "Product", render: (r) => nameOf(products, r.product_id) },
    { key: "category", header: "Category", render: (r) => <span className="capitalize">{(r.category || "").replace(/_/g, " ")}</span> },
    { key: "amount_ex", header: "Ex-GST", align: "right", mono: true, render: (r) => fmtMoney(r.amount_ex) },
    { key: "gst", header: "GST", align: "right", mono: true, render: (r) => fmtMoney(r.gst) },
    { key: "amount_inc", header: "Inc-GST", align: "right", mono: true, render: (r) => fmtMoney(r.amount_inc) },
    { key: "recurring", header: "Type", render: (r) => <span className="text-xs text-slate-400">{r.recurring ? "Recurring" : "One-off"}</span> },
    { key: "payment_status", header: "Status", render: (r) => <StatusBadge status={r.payment_status} /> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }} data-testid={`edit-revenue-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={(e) => { e.stopPropagation(); del.mutate(r.id); }} data-testid={`delete-revenue-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Revenue" subtitle="Recorded invoices and recurring revenue" testId="revenue-header">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-revenue-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Revenue
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={rows} testId="revenue-ledger-table" empty={{ title: "No revenue recorded", message: "Add your first invoice or recurring revenue record." }} />
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Revenue" : "Add Revenue"} fields={fields} initial={editing} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="revenue-form" />
    </div>
  );
}
